/**
 * Service worker registration with build revision and reload when a new worker activates.
 */

let reloadScheduled = false;

function scheduleReloadAfterSwUpdate(): void {
  if (reloadScheduled) return;
  reloadScheduled = true;
  globalThis.location.reload();
}

/** Listens for new worker activation (cache revision bump). */
export function bindOfflineServiceWorkerReload(): void {
  const sw = globalThis.navigator?.serviceWorker;
  if (!sw) return;
  sw.addEventListener('message', (event) => {
    if (event.data?.type === 'APP_SW_ACTIVATED') scheduleReloadAfterSwUpdate();
  });
  sw.addEventListener('controllerchange', () => scheduleReloadAfterSwUpdate());
}

function offlineServiceWorkerRegisterUrl(baseUrl: string, buildRevision: string): string {
  const rev = buildRevision.trim();
  if (!rev) return baseUrl;
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}rev=${encodeURIComponent(rev)}`;
}

/** Dev / SW-off: drop stale PWA caches so Vite HMR bundle is not shadowed. */
async function purgeLegacyOfflineShell(): Promise<void> {
  const sw = globalThis.navigator?.serviceWorker;
  if (sw) {
    const regs = typeof sw.getRegistrations === 'function' ? await sw.getRegistrations() : [];
    if (regs.length > 0) {
      await Promise.all(regs.map((reg) => reg.unregister()));
    } else {
      const reg = await sw.getRegistration();
      if (reg) await reg.unregister();
    }
  }
  if (!globalThis.caches) return;
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
}

/** Registers the offline shell service worker (no-op when disabled or unavailable). */
export async function registerOfflineServiceWorkerFromEnv(env: {
  ADMIN_OFFLINE_SERVICE_WORKER: string;
  ADMIN_OFFLINE_SW_URL: string;
  ADMIN_OFFLINE_SW_SCOPE: string;
  ADMIN_OFFLINE_SW_READY_TIMEOUT_MS: string;
  ADMIN_BUILD_REVISION: string;
}): Promise<void> {
  const sw = globalThis.navigator?.serviceWorker;
  if (!sw || env.ADMIN_OFFLINE_SERVICE_WORKER === '0') {
    if (env.ADMIN_OFFLINE_SERVICE_WORKER === '0') await purgeLegacyOfflineShell();
    return;
  }

  bindOfflineServiceWorkerReload();

  const base = (env.ADMIN_OFFLINE_SW_URL ?? '').trim() || '/offline-sw.js';
  const swUrl = offlineServiceWorkerRegisterUrl(base, env.ADMIN_BUILD_REVISION ?? '');
  const swScope = (env.ADMIN_OFFLINE_SW_SCOPE ?? '').trim() || '/';
  const readyMs = Math.max(
    0,
    Math.floor(Number.parseInt((env.ADMIN_OFFLINE_SW_READY_TIMEOUT_MS ?? '').trim() || '2500', 10) || 2500),
  );
  try {
    const reg = await sw.register(swUrl, { scope: swScope, updateViaCache: 'none' });
    void reg.update().catch(() => {
      /* optional */
    });
    await Promise.race([sw.ready, new Promise<void>((r) => setTimeout(r, readyMs))]);
  } catch {
    /* file://, CSP */
  }
}
